import { useTranslation } from 'react-i18next';

export interface OmissionRow {
  id: string;
  name: string;
  /** Personas de la unidad que recibieron al menos una encuesta ya cerrada. */
  peopleCount: number;
  /** Encuestas cerradas de la unidad: respondidas + omitidas + expiradas. */
  evaluations: number;
  omittedCount: number;
  /** Porcentaje 0–100 de omisión sobre las encuestas cerradas. */
  omissionRate: number;
}

interface OmissionsCardProps {
  omissions: OmissionRow[];
}

/** Máximo del eje de la mini-barra: por encima de este porcentaje la barra se llena por completo. */
const OMISSION_BAR_CEILING = 30;

function omitColor(rate: number): string {
  if (rate > 12) return 'var(--cp-danger-light)';
  if (rate > 7) return '#f59e0b';
  return '#10b981';
}

function downloadCsv(rows: OmissionRow[], headers: string[]): void {
  const lines = [headers, ...rows.map((row) => [
    row.name,
    String(row.peopleCount),
    String(row.evaluations),
    String(row.omittedCount),
    `${row.omissionRate}%`,
  ])];
  const csv = lines.map((cells) => cells.map((cell) => `"${cell}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = 'omisiones.csv';
  anchor.click();
  URL.revokeObjectURL(objectUrl);
}

/**
 * Omisiones por unidad organizacional.
 *
 * Las cifras vienen del conteo de instancias por estado de SurveyService: antes la tarjeta solo
 * recibía el número de personas sin respuestas y mostraba personas, evaluaciones y tasa en cero
 * aunque sí hubiera omisiones registradas.
 */
export function OmissionsCard({ omissions }: OmissionsCardProps) {
  const { t } = useTranslation();

  // Una sola fuente para el encabezado de la tabla, el `data-label` de cada celda (que es lo
  // que se muestra en celular) y las columnas del CSV: si divergen, el archivo descargado deja
  // de coincidir con lo que el usuario vio en pantalla.
  const columnLabels = {
    unit: t('analytics.heatmap.unit'),
    people: t('analytics.summary.omitPeople'),
    evaluations: t('analytics.kpi.evaluations'),
    omitted: t('analytics.summary.omittedSurveys'),
    rate: t('analytics.summary.omitRate'),
  };
  const csvHeaders = Object.values(columnLabels);

  return (
    <div className="au-card">
      <div className="au-card-header">
        <div>
          <div className="au-card-title">
            <i className="fas fa-forward" aria-hidden="true" />
            {t('analytics.summary.omissionsByUnit')}
          </div>
          <div className="au-card-subtitle">{t('analytics.summary.omissionsSubtitle')}</div>
        </div>
        <button
          type="button"
          className="au-omit-excel-btn"
          disabled={omissions.length === 0}
          onClick={() => downloadCsv(omissions, csvHeaders)}
        >
          <i className="fas fa-file-excel" style={{ color: '#217346' }} aria-hidden="true" />
          {t('analytics.summary.downloadExcel')}
        </button>
      </div>

      <div className="au-omit-table-wrap">
        <table className="au-omit-table">
          <thead>
            <tr>
              <th scope="col">{columnLabels.unit}</th>
              <th scope="col">{columnLabels.people}</th>
              <th scope="col">{columnLabels.evaluations}</th>
              <th scope="col">{columnLabels.omitted}</th>
              <th scope="col">{columnLabels.rate}</th>
            </tr>
          </thead>
          <tbody>
            {omissions.length === 0 ? (
              <tr>
                <td colSpan={5} className="au-omit-empty">
                  <i className="fas fa-check-circle" aria-hidden="true" />
                  {t('analytics.summary.noOmissions')}
                </td>
              </tr>
            ) : (
              omissions.map((row) => {
                const rateColor = omitColor(row.omissionRate);
                const barWidth = Math.min(row.omissionRate, OMISSION_BAR_CEILING)
                  / OMISSION_BAR_CEILING * 100;
                return (
                  <tr key={row.id} className={row.omissionRate > 12 ? 'warning-row' : undefined}>
                    <td data-label={columnLabels.unit}>
                      <span className="au-omit-unit-name">{row.name}</span>
                    </td>
                    <td data-label={columnLabels.people} className="au-omit-meta">
                      {row.peopleCount}
                    </td>
                    <td data-label={columnLabels.evaluations} className="au-omit-meta">
                      {row.evaluations.toLocaleString()}
                    </td>
                    <td data-label={columnLabels.omitted} className="au-omit-meta">
                      {row.omittedCount.toLocaleString()}
                    </td>
                    <td data-label={columnLabels.rate}>
                      <div className="au-omit-rate-cell">
                        <div className="au-omit-mini-bar">
                          <div
                            className="au-omit-mini-fill"
                            style={{ width: `${barWidth}%`, background: rateColor }}
                          />
                        </div>
                        <span className="au-omit-rate-value" style={{ color: rateColor }}>
                          {row.omissionRate}%
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
